import { useEffect, useState } from "react";
import { Modal } from "@telluscompany/design-system";
import { Eye } from "lucide-react";

import { useLocalization } from "@i18n";

import {
  useInspectionDetailQuery,
  type InspectionCapabilities,
  type InspectionDetail,
} from "../../api/inspection";
import type { InspectionFormMode } from "../../catalog/inspection-form-bundle";
import { getInspectionFormBundle } from "../../catalog/registry";
import { isInspectionFormReadOnly } from "../../utils/inspectionFormAccess";
import { inspectionStatusLabelKey } from "../../utils/inspectionLabels";
import "../../styles/inspection-ui.css";

export type InspectionFormModalMode = InspectionFormMode;

export interface InspectionFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  structureId: string;
  inspectionId: string;
  mode: InspectionFormModalMode;
  capabilities: InspectionCapabilities;
}

/**
 * Slot da ficha (form §1.1.1 / decisions I2): GET Y=5 →
 * `getInspectionFormBundle(templateVersion)` → `Form` da pasta. Nova inspeção
 * é outro componente (`InspectionCreateModal`).
 */
const InspectionFormModal = ({
  isOpen,
  onClose,
  structureId,
  inspectionId,
  mode,
  capabilities,
}: InspectionFormModalProps) => {
  const { translate } = useLocalization();
  const [detail, setDetail] = useState<InspectionDetail | null>(null);

  const {
    data: fetchedDetail,
    error,
    refetch,
  } = useInspectionDetailQuery(structureId, inspectionId);

  useEffect(() => {
    if (fetchedDetail) setDetail(fetchedDetail);
  }, [fetchedDetail]);

  const formVersion = detail?.templateVersion;
  const bundle = formVersion ? getInspectionFormBundle(formVersion) : null;
  const VersionForm = bundle?.Form;

  const isReadOnly = isInspectionFormReadOnly(mode, capabilities, detail);

  const unsupportedContent = (
    <div className="flex flex-col gap-2 py-6 text-center text-sm text-[var(--insp-muted,#808080)]">
      <p role="alert" className="text-red-600">
        {translate("inspection.form.unsupportedVersion", {
          version: formVersion ?? "",
        })}
      </p>
    </div>
  );

  const loadingContent = (
    <div className="flex flex-col gap-2 py-6 text-center text-sm text-[var(--insp-muted,#808080)]">
      {error?.parsedMessage ? (
        <p role="alert" className="text-red-600">
          {error.parsedMessage}
        </p>
      ) : (
        <p>{translate("inspection.loading")}</p>
      )}
    </div>
  );

  const fichaContent = !detail
    ? loadingContent
    : VersionForm
      ? (
          <VersionForm
            structureId={structureId}
            inspectionId={inspectionId}
            mode={mode}
            capabilities={capabilities}
            detail={detail}
            onDetailChange={setDetail}
            onRefetchDetail={() => {
              void refetch();
            }}
            onClose={onClose}
          />
        )
      : unsupportedContent;

  const fichaTitle = detail
    ? translate("inspection.form.titleWithCode", {
        code: detail.inspectionCode,
      })
    : translate("inspection.form.title");

  const fichaTitleNode =
    isReadOnly && detail ? (
      <div className="insp-ficha-title-block">
        <span>{fichaTitle}</span>
        <span className="insp-readonly-flag">
          <Eye aria-hidden size={14} />
          {translate("inspection.form.readOnlyWithStatus", {
            status: translate(inspectionStatusLabelKey(detail.inspectionStatus)),
          })}
        </span>
      </div>
    ) : (
      fichaTitle
    );

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={fichaTitleNode}
      width="1160px"
      content={fichaContent}
    />
  );
};

export default InspectionFormModal;
